import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Caption1, Spinner, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { COVERAGE_MAP_HEIGHT, COVERAGE_MAP_LETTERS, COVERAGE_MAP_MAX_PINS, COVERAGE_MAP_WIDTH, type CoverageMapResponse } from '@vigeo/shared';
import { ShellIcons, tokens, useShellTier } from '@vigeo/ui';
import { fetchCoverageMap } from '@/lib/api';

/**
 * The Coverage card (ADR-018 Q8 "MP safe", #474; Figma page 09 "Roster · Coverage map v3", Concept A, frames A1 to A5, #563).
 *
 * A CARD BESIDE THE STAFF LIST: the map picture, and under it every place staff are assigned to with its letter and its
 * count. The map, this list, the Place filter and the staff list all show ONE selection: the roster's own filter. Nothing
 * here keeps a selection of its own.
 *
 * THE PICTURE IS STILL A PICTURE. It is an `<img>` from Google's Static Maps: no Google script, nothing to zoom or drag.
 * What can be clicked are OUR OWN lettered pins, plain buttons laid over the picture at the positions our server worked out
 * (`apps/api/src/integrations/google/static-map.ts`). So the ring on a chosen pin and the hollow pin of a place nobody
 * matches are drawn here: choosing a place, or changing a filter, loads no new picture and tells Google nothing.
 * NOT CHECKED BY ANYONE: whether Google's terms allow our own pins drawn over a Static Maps picture (#563, open point 1).
 *
 * WHAT THE BROWSER ASKS. Nothing while the map is hidden, and nothing when nobody has a place (`COVERAGE_MAP_STARTS_OPEN`). Then our server once, for the picture's
 * address and the positions, and Google once, for the picture. The letters are given here, A to Z by name, so the list and
 * the staff rows carry them whether or not the map has been opened.
 *
 * THE LIST IS COMPLETE. Every place and its count is a row, with or without a pin, so a crowded or missing pin is still
 * reachable.
 */

/**
 * THE ONE SWITCH (#563, open point 2). `false`: the map starts closed on every visit and nothing is asked of our server,
 * or of Google, until "Show map" is pressed. Julio, 2026-10-09, on #563: "start closed for now". An always-open picture
 * reads up to 26 places from Google on every Roster visit and uses up the daily allowance (review of #564, finding 5); it
 * stays closed until the live map (ADR-021, #580) replaces the picture. This is also decision MP3. `true`: the map is open
 * when Roster opens, as frames A1 to A5 draw it (his answer of 2026-10-08, withdrawn the next day).
 * Opening or hiding is never remembered: every visit starts as this says.
 */
export const COVERAGE_MAP_STARTS_OPEN = false;

const useStyles = makeStyles({
  card: {
    display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: tokens.spacingVerticalM, alignContent: 'start', boxSizing: 'border-box', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadowCard, padding: tokens.spacingHorizontalL,
  },
  // Frame A5: when the card has the page's whole width the picture and the list sit side by side.
  cardWide: { gridTemplateColumns: 'minmax(0, 20rem) minmax(0, 1fr)', columnGap: tokens.spacingHorizontalXL },
  head: { gridColumnStart: 1, gridColumnEnd: -1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  headWords: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  title: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold },
  quiet: { color: tokens.colorNeutralForeground3 },
  picture: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  frame: {
    position: 'relative', width: '100%', aspectRatio: `${COVERAGE_MAP_WIDTH} / ${COVERAGE_MAP_HEIGHT}`, boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground3, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusLarge, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  img: { position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' },
  /**
   * A pin: a circle with its MIDDLE on the place (the server keeps room for it at the picture's edges). The button is the
   * size of the circle, so what is drawn is what is clicked; on a phone the button is the touch size around the same circle.
   */
  pin: {
    position: 'absolute', transform: 'translate(-50%, -50%)', zIndex: 1, boxSizing: 'border-box', padding: 0, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', border: 'none', borderRadius: tokens.borderRadiusCircular,
    ':hover': { zIndex: 3 },
    ':focus-visible': { zIndex: 3, outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandForeground1}`, outlineOffset: tokens.strokeWidthThick },
  },
  pinTouch: { minWidth: '2.75rem', minHeight: '2.75rem' },
  pinChosenLayer: { zIndex: 2 },
  pinFace: {
    boxSizing: 'border-box', width: '1.75rem', height: '1.75rem',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular, border: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralBackground1}`, boxShadow: tokens.shadow4,
    backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    fontFamily: 'inherit', fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold,
  },
  // Nobody at this place matches the filters: the pin stays where it is, drawn hollow (frame A3).
  pinHollow: { backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}` },
  // The chosen pin: bigger, with a dark ring that stays (decision RG), above its neighbours.
  pinChosen: { width: '2.5rem', height: '2.5rem', border: `${tokens.strokeWidthThickest} solid ${tokens.colorNeutralForeground1}`, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  // The chosen place's name beside its pin, on the side with more room.
  pinName: {
    position: 'absolute', zIndex: 2, transform: 'translateY(-50%)', pointerEvents: 'none', whiteSpace: 'nowrap', maxWidth: '45%', overflow: 'hidden', textOverflow: 'ellipsis',
    paddingBlock: tokens.spacingVerticalXXS, paddingInline: tokens.spacingHorizontalS, borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackgroundInverted, color: tokens.colorNeutralForegroundInverted,
    fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold,
  },
  notice: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, alignItems: 'flex-start', boxSizing: 'border-box',
    backgroundColor: tokens.colorStatusWarningBackground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorStatusWarningBorder1}`, borderRadius: tokens.borderRadiusLarge, padding: tokens.spacingHorizontalL,
  },
  // Frame A4: nobody has a place, so there is no map to show and none is asked for.
  empty: {
    gridColumnStart: 1, gridColumnEnd: -1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalS, boxSizing: 'border-box',
    border: `${tokens.strokeWidthThin} dashed ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusLarge, paddingBlock: tokens.spacingVerticalXXL, paddingInline: tokens.spacingHorizontalXXL,
  },
  emptyWords: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  list: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: 0 },
  listWide: { gridColumnStart: 1, gridColumnEnd: -1 },
  listHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, paddingInline: tokens.spacingHorizontalS },
  listTitle: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  rows: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, maxHeight: '30rem', overflowY: 'auto' },
  row: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, width: '100%', boxSizing: 'border-box', minHeight: '2.5rem', textAlign: 'start',
    paddingBlock: tokens.spacingVerticalXS, paddingInline: tokens.spacingHorizontalS,
    backgroundColor: 'transparent', color: tokens.colorNeutralForeground1, border: `${tokens.strokeWidthThin} solid transparent`, borderRadius: tokens.borderRadiusMedium,
    fontFamily: 'inherit', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300,
  },
  rowButton: {
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorBrandForeground1}`, outlineOffset: tokens.spacingHorizontalXXS },
  },
  rowTouch: { minHeight: '2.75rem' },
  chosen: { backgroundColor: tokens.colorBrandBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorBrandStroke1}`, fontWeight: tokens.fontWeightSemibold, ':hover': { backgroundColor: tokens.colorBrandBackground2 } },
  rowHollow: { color: tokens.colorNeutralForeground3 },
  words: { flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowWrap: 'anywhere' },
  count: { color: tokens.colorNeutralForeground2, flexShrink: 0, fontWeight: tokens.fontWeightRegular },
  rowX: { display: 'inline-flex', flexShrink: 0, color: tokens.colorNeutralForeground2 },
  letter: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '1.5rem', height: '1.5rem', boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold,
  },
  letterSmall: { width: '1.125rem', height: '1.125rem', fontSize: tokens.fontSizeBase100, lineHeight: tokens.lineHeightBase100 },
  // A letter past Z ("AA", the live map's 27th place, #689): as high, and as wide as its two letters need.
  letterLong: { width: 'auto', paddingInline: tokens.spacingHorizontalXS },
  letterHollow: { backgroundColor: tokens.colorNeutralBackground1, color: tokens.colorNeutralForeground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}` },
  letterNone: { backgroundColor: 'transparent', border: `${tokens.strokeWidthThin} dashed ${tokens.colorNeutralStroke1}` },
  legend: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: tokens.spacingHorizontalL, rowGap: tokens.spacingVerticalXS, paddingInline: tokens.spacingHorizontalS, paddingBlockStart: tokens.spacingVerticalS, color: tokens.colorNeutralForeground3 },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  // The button's two wordings share one grid cell, so the cell (and the button) is as wide as the longer one.
  wordings: { display: 'inline-grid', justifyItems: 'center' },
  wording: { gridRowStart: 1, gridColumnStart: 1 },
  wordingNotInUse: { visibility: 'hidden' },
});

export interface CoverageMapPlace {
  readonly placeId: string;
  /** The place's words from the app's own list, or "Pick this place again" for a code that is not in it (#594). */
  readonly label: string;
  /** Its pin's letter, or null for a place past the 26th. */
  readonly letter: string | null;
  /** People on the roster who hold it. */
  readonly total: number;
  /** Of those, the people who match every filter other than the place itself. Equal to `total` when no filter is on. */
  readonly matching: number;
}

export interface CoverageMapProps {
  readonly id: string;
  /** Every place on the roster, in the order of the letters (`letterOrder`; the caller sorts). */
  readonly places: readonly CoverageMapPlace[];
  /** Staff with no place at all. */
  readonly noPlace: { readonly total: number; readonly matching: number };
  /** Everyone on the roster, and how many of them hold at least one place. */
  readonly staff: number;
  readonly staffWithPlace: number;
  /** Whether a filter other than the place is on: counts then read "1 of 2". */
  readonly filtered: boolean;
  /** The places the roster is filtered to. */
  readonly chosen: readonly string[];
  /**
   * Whether the staff list is showing its "Show everyone" button right now. It is not when a typed area is chosen with the
   * place, or when nobody matches and the list is replaced: the line under the picture then does not name the button.
   */
  readonly showEveryoneOffered: boolean;
  /** A pin or a row was clicked. What that does to the filter is the roster's to say (`choosePlace`). */
  readonly onChoose: (placeId: string) => void;
  readonly open: boolean;
  readonly onToggle: () => void;
  /** False while any name is still being read: the picture is not asked for until the order cannot change. */
  readonly ready?: boolean;
}

export const COVERAGE_MAP_UNAVAILABLE_TITLE = 'The coverage map is not available right now.';
export const COVERAGE_MAP_UNAVAILABLE_BODY = 'The map picture could not be loaded. The list of places and the roster still work.';
export const NOT_ON_PICTURE = 'Not on the picture';
export const COVERAGE_MAP_SHOW = 'Show map';
export const COVERAGE_MAP_HIDE = 'Hide map';
export const NO_PLACE_YET = 'No place yet';

/**
 * THE ORDER OF THE LETTERS, in one place (decision `ML alpha`, Julio 2026-10-06): A to Z by the place's name AS HR SEES IT
 * (a ZIP code by its text, "ZIP 23220"), ignoring case, with the place id as the tie-break so the order is stable. NOTHING
 * about staff, and no count, is used. This decides which place is A, and, with more than 26 places, which 26 get a pin
 * (the first 26 in this order).
 */
export function letterOrder(places: readonly { readonly placeId: string; readonly label: string }[]): string[] {
  return [...places]
    .sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }) || (a.placeId < b.placeId ? -1 : a.placeId > b.placeId ? 1 : 0))
    .map((place) => place.placeId);
}

/** The letter of each place, given the places in `letterOrder`: A for the first, Z for the 26th, none after that. */
export function pinLetters(ordered: readonly string[]): Map<string, string> {
  return new Map(ordered.slice(0, COVERAGE_MAP_MAX_PINS).map((placeId, index) => [placeId, COVERAGE_MAP_LETTERS[index]!] as const));
}

const people = (count: number) => (count === 1 ? '1 person' : `${count} people`);
/** A row's count: "2 people", or "1 of 2" while a filter is on. */
export const countWords = (count: { readonly total: number; readonly matching: number }, filtered: boolean): string => (filtered ? `${count.matching} of ${count.total}` : people(count.total));

/** The line under "Coverage" (frames A1, A3 and A4). */
export function coverageSummary(input: { readonly places: readonly CoverageMapPlace[]; readonly staff: number; readonly staffWithPlace: number; readonly filtered: boolean }): string {
  const { places, staff, staffWithPlace, filtered } = input;
  const have = `${staffWithPlace} of ${staff} staff have a place`;
  if (places.length === 0) return have;
  if (filtered) return `${places.filter((place) => place.matching > 0).length} of ${places.length} places have matching staff`;
  return `${places.length === 1 ? '1 place' : `${places.length} places`} · ${have}`;
}

/** The line under the picture: what a click does, what is selected and how to clear it, or what the hollow pins mean. */
export function coverageHint(input: { readonly places: readonly CoverageMapPlace[]; readonly chosen: readonly string[]; readonly filtered: boolean; readonly open: boolean; /** Whether the "Show everyone" button is on screen: the words name it only then. */ readonly canShowEveryone: boolean }): string {
  const { places, chosen, filtered, open } = input;
  const chosenPlaces = places.filter((place) => chosen.includes(place.placeId));
  if (chosenPlaces.length === 1) return `${chosenPlaces[0]!.label} is selected. Click it again${input.canShowEveryone ? ', or “Show everyone”,' : ''} to clear.`;
  if (chosenPlaces.length > 1) return `${chosenPlaces.length} places are selected. ${input.canShowEveryone ? '“Show everyone” clears them.' : 'Take them out of the Place filter to clear.'}`;
  if (filtered) {
    const nobody = places.filter((place) => place.matching === 0);
    const pin = open ? 'pin is' : 'letter is';
    const pins = open ? 'pins are' : 'letters are';
    if (nobody.length === 0) return `The ${open ? 'map follows' : 'places follow'} the filters.`;
    if (nobody.length === 1) return `The ${open ? 'map follows' : 'places follow'} the filters. ${nobody[0]!.label} has nobody who matches, so its ${pin} hollow.`;
    return `The ${open ? 'map follows' : 'places follow'} the filters. ${nobody.length} places have nobody who matches, so their ${pins} hollow.`;
  }
  return open ? 'Click a pin or a place to see who works there.' : 'Click a place to see who works there.';
}

/** A place's letter in a circle, as on its pin: in the places list, beside a person's places and in the staff heading. */
export function PinLetter({ letter, hollow = false, small = false }: { readonly letter: string | null; readonly hollow?: boolean; readonly small?: boolean }) {
  const styles = useStyles();
  return <span className={mergeClasses(styles.letter, small && styles.letterSmall, letter !== null && letter.length > 1 && styles.letterLong, letter === null ? styles.letterNone : hollow && styles.letterHollow)} aria-hidden="true">{letter ?? ''}</span>;
}

/**
 * The button that shows and hides the map. It holds BOTH wordings, one over the other, and only the one in use can be seen or
 * read by assistive technology. So the button is the same width whichever it says, and pressing it moves nothing beside it.
 */
function MapButton({ open, onToggle, controls }: { readonly open: boolean; readonly onToggle: () => void; /** The id of what it shows. */ readonly controls: string }) {
  const styles = useStyles();
  return (
    <Button aria-expanded={open} aria-controls={controls} onClick={onToggle}>
      <span className={styles.wordings}>
        {[COVERAGE_MAP_SHOW, COVERAGE_MAP_HIDE].map((wording) => {
          const inUse = wording === (open ? COVERAGE_MAP_HIDE : COVERAGE_MAP_SHOW);
          return <span key={wording} className={mergeClasses(styles.wording, !inUse && styles.wordingNotInUse)} aria-hidden={inUse ? undefined : true}>{wording}</span>;
        })}
      </span>
    </Button>
  );
}

export function useCoverageMap(placeIds: readonly string[], enabled = true) {
  return useQuery<CoverageMapResponse>({
    // The ORDER is part of the question (it decides which 26 are read), so it is part of the key.
    queryKey: ['coverage-map', placeIds.join(',')],
    queryFn: () => fetchCoverageMap(placeIds),
    // Not before the map is open, and not before the names are in: the order is by name, and a name arriving later would change the question (a second billed read).
    enabled: enabled && placeIds.length > 0,
    retry: false,
    // Positions are held in this page only, for as long as it is open (never written to browser storage): hiding the map
    // and showing it again, at any time, asks nothing. Each opening costs up to 26 Place Details reads of a 100-a-day quota.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

const percent = (shareOf: number) => `${(shareOf * 100).toFixed(2)}%`;

export function CoverageMap({ id, places, noPlace, staff, staffWithPlace, filtered, chosen, showEveryoneOffered, onChoose, open, onToggle, ready = true }: CoverageMapProps) {
  const styles = useStyles();
  const tier = useShellTier();
  // Below the desktop tier the page stacks (the card gets its whole width), and on a phone the card stacks too.
  const wide = tier === 'tablet';
  const touch = tier === 'mobile';
  const query = useCoverageMap(places.map((p) => p.placeId), open && ready);
  /** The address the browser last failed to load a picture from, so the notice says so without a second request. */
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  /** Which try at loading the picture this is: a new one remounts the `<img>`, which asks Google for the picture again. */
  const [attempt, setAttempt] = useState(0);
  const data = query.data;
  const src = data?.pictureUrl ?? null;
  const unavailable = query.isError || (src !== null && failedSrc === src);
  const loading = !unavailable && (query.isPending || (src !== null && loadedSrc !== src));
  const byId = new Map(places.map((place) => [place.placeId, place] as const));
  const pinned = new Set((data?.pins ?? []).map((pin) => pin.placeId));
  // Try again asks for what failed. If OUR server's answer failed, ask it again. If the PICTURE failed, the answer was fine
  // (the same address would come back, at up to 26 Google reads) so only the picture is asked for again.
  const retry = () => {
    setFailedSrc(null);
    if (query.isError) void query.refetch();
    else setAttempt((n) => n + 1);
  };
  const hollowLetter = places.find((place) => place.matching === 0 && place.letter !== null)?.letter ?? null;
  const pictureId = `${id}-picture`;

  return (
    <section id={id} className={mergeClasses(styles.card, wide && places.length > 0 && open && styles.cardWide)} aria-label="Coverage">
      <div className={styles.head}>
        <div className={styles.headWords}>
          <h2 className={styles.title}>Coverage</h2>
          <Caption1 className={styles.quiet}>{coverageSummary({ places, staff, staffWithPlace, filtered })}</Caption1>
        </div>
        {places.length > 0 ? <MapButton open={open} onToggle={onToggle} controls={pictureId} /> : null}
      </div>

      {places.length === 0 ? (
        <div className={styles.empty}>
          <PinLetter letter={null} />
          <Text weight="semibold" size={400}>No places yet</Text>
          <div className={styles.emptyWords}>
            <Text>Nobody on the roster has a place. Press Edit on a staff member and add a city or a ZIP code. It then appears here as a pin, with the people who work there.</Text>
            <Caption1 className={styles.quiet}>No map is asked of Google until somebody has a place.</Caption1>
          </div>
        </div>
      ) : (
        <div id={pictureId} className={mergeClasses(styles.picture, !open && styles.listWide)}>
          {!open ? null : unavailable ? (
            <div className={styles.notice} role="status">
              <Text weight="semibold">{COVERAGE_MAP_UNAVAILABLE_TITLE}</Text>
              <Text>{COVERAGE_MAP_UNAVAILABLE_BODY}</Text>
              <Button onClick={retry}>Try again</Button>
            </div>
          ) : (
            <div className={styles.frame} aria-busy={loading} role="group" aria-label="Coverage map">
              {loading ? <Spinner size="small" label="Loading the map" /> : null}
              {src === null ? null : (
                <img
                  key={`${src}#${attempt}`}
                  className={styles.img}
                  src={src}
                  width={COVERAGE_MAP_WIDTH}
                  height={COVERAGE_MAP_HEIGHT}
                  alt="A Google map of the places staff are assigned to. Each place has a lettered pin over it, and the list has the same letters."
                  onLoad={() => setLoadedSrc(src)}
                  onError={() => setFailedSrc(src)}
                />
              )}
              {data === undefined || loading ? null : data.pins.map((pin) => {
                const place = byId.get(pin.placeId);
                if (place === undefined || place.letter === null) return null;
                const isChosen = chosen.includes(pin.placeId);
                return (
                  <span key={pin.placeId}>
                    <button
                      type="button"
                      className={mergeClasses(styles.pin, touch && styles.pinTouch, isChosen && styles.pinChosenLayer)}
                      style={{ left: percent(pin.x), top: percent(pin.y) }}
                      aria-pressed={isChosen}
                      aria-label={`Pin ${place.letter}: ${place.label}, ${countWords(place, filtered)}`}
                      onClick={() => onChoose(pin.placeId)}
                    >
                      <span className={mergeClasses(styles.pinFace, place.matching === 0 && styles.pinHollow, isChosen && styles.pinChosen)}>{place.letter}</span>
                    </button>
                    {isChosen ? (
                      // Its name, on the side of the pin with more room. Said already by the button's name, so hidden from assistive technology.
                      <span
                        className={styles.pinName}
                        aria-hidden="true"
                        style={pin.x > 0.5 ? { right: `calc(${percent(1 - pin.x)} + 1.5rem)`, top: percent(pin.y) } : { left: `calc(${percent(pin.x)} + 1.5rem)`, top: percent(pin.y) }}
                      >
                        {place.label}
                      </span>
                    ) : null}
                  </span>
                );
              })}
            </div>
          )}
          <Caption1>{coverageHint({ places, chosen, filtered, open, canShowEveryone: showEveryoneOffered })}</Caption1>
        </div>
      )}

      <div className={mergeClasses(styles.list, !(wide && places.length > 0 && open) && styles.listWide)}>
        <div className={styles.listHead}>
          <h3 className={styles.listTitle}>Places ({places.length})</h3>
          <Caption1 className={styles.quiet}>{filtered ? 'Matching staff' : 'Staff'}</Caption1>
        </div>
        <ul className={styles.rows} aria-label="Places">
          {places.map((place) => {
            const isChosen = chosen.includes(place.placeId);
            const offPicture = place.letter === null || (open && data !== undefined && !pinned.has(place.placeId));
            return (
              <li key={place.placeId}>
                <button type="button" className={mergeClasses(styles.row, styles.rowButton, touch && styles.rowTouch, place.matching === 0 && styles.rowHollow, isChosen && styles.chosen)} aria-pressed={isChosen} onClick={() => onChoose(place.placeId)}>
                  <PinLetter letter={place.letter} hollow={place.matching === 0} />
                  <span className={styles.words}>
                    <span>{place.label}</span>
                    {offPicture ? <Caption1>{NOT_ON_PICTURE}</Caption1> : null}
                  </span>
                  <span className={styles.count}>{countWords(place, filtered)}</span>
                  {isChosen ? <span className={styles.rowX} aria-hidden="true"><ShellIcons.close /></span> : null}
                </button>
              </li>
            );
          })}
          {noPlace.total === 0 ? null : (
            // Not a button: "no place" is not a place, and no filter for it is designed (#563).
            <li className={mergeClasses(styles.row, touch && styles.rowTouch)}>
              <PinLetter letter={null} />
              <span className={styles.words}>{NO_PLACE_YET}</span>
              <span className={styles.count}>{countWords(noPlace, filtered)}</span>
            </li>
          )}
        </ul>
        {places.length === 0 ? null : (
          <div className={styles.legend}>
            <span className={styles.legendItem}><PinLetter letter="A" small /><Caption1>A place, lettered A to Z by name</Caption1></span>
            {filtered && hollowLetter !== null ? <span className={styles.legendItem}><PinLetter letter={hollowLetter} hollow small /><Caption1>Nobody matches</Caption1></span> : null}
          </div>
        )}
      </div>
    </section>
  );
}
